import { get, isNull } from "lodash-es";
import { DateTime } from "luxon";
import posthog from "posthog-js";
import React, { FC, useState } from "react";
import { useSearchParams } from "react-router-dom";

import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";

import { ConfirmationDialog } from "../../../components/confirmation-dialog";
import { useDialog } from "../../../hooks/use-dialog";
import { setClusterAndOrg } from "../../../slices/current";
import { useDispatch, useSelector } from "../../../store";
import {
  Organization,
  useDeleteOrganizationMutation,
  useLeaveOrganizationMutation,
  useListOrganizationsQuery,
} from "../../../store/pomeriumApi";
import ChangeOwner, { ChangeOwnerParams } from "./ChangeOwner";

const List: FC = () => {
  const [, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const ownerDialog = useDialog<ChangeOwnerParams>();
  const { organizationId } = useSelector((state) => state.current);
  const { clustersByOrg } = useSelector((state) => state.organizations);
  const [confirmLeave, setConfirmLeave] = useState<string>("");
  const [orgForDeletion, setOrgForDeletion] = useState<Organization | null>(
    null,
  );
  const [leave] = useLeaveOrganizationMutation();
  const [deleteOrg] = useDeleteOrganizationMutation();
  const { data: orgs } = useListOrganizationsQuery();
  const changeOwner = (orgId: string, name: string, ownerId: string) => {
    ownerDialog.handleOpen({ orgId, name, ownerId });
  };

  const deleteOrganization = () => {
    if (orgForDeletion?.id === organizationId) {
      const personalOrgs = orgs?.find(
        (o) => o?.organizationType === "personal",
      );
      const personalOrgId = get(personalOrgs, "id", "");
      const cId = get(clustersByOrg, personalOrgId + ".0.id", "") as string;
      dispatch(
        setClusterAndOrg({
          clusterId: cId,
          organizationId: personalOrgId,
        }),
      );
      setSearchParams(
        (params) => {
          params.set("oid", personalOrgId);
          params.set("cid", cId);
          return params;
        },
        { replace: true },
      );
    }
    deleteOrg(orgForDeletion?.id || "");
    setOrgForDeletion(null);
  };

  const leaveOrg = (organizationId: string) => () => {
    setConfirmLeave(organizationId);
    posthog.capture("left_organization", { organizationId });
  };

  return (
    <div style={{ width: "100%" }}>
      <ConfirmationDialog
        message="Are you sure?"
        open={!!confirmLeave}
        onCancel={() => setConfirmLeave("")}
        onConfirm={() => leave(confirmLeave)}
        title="Permanently Leave Organization?"
        variant="warning"
      />
      <ConfirmationDialog
        message={
          "Are you sure you wish to delete " +
          (orgForDeletion?.name || orgForDeletion?.id) +
          " ? This can't be undone!"
        }
        open={!isNull(orgForDeletion)}
        onCancel={() => setOrgForDeletion(null)}
        onConfirm={deleteOrganization}
        title="Permanently Delete Organization?"
        variant="warning"
      />
      <ChangeOwner dialog={ownerDialog} />

      <Table>
        <TableBody>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Join Date</TableCell>
            <TableCell>Permissions</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableBody>
        <TableBody>
          {orgs?.map((org) => (
            <TableRow
              key={"org_" + org.id}
              sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
            >
              <TableCell component="th" scope="row">
                {org.name}
              </TableCell>
              <TableCell>
                {DateTime.fromISO(org.joinedAt).toLocaleString()}
              </TableCell>
              <TableCell>{org.role}</TableCell>
              <TableCell align="center">
                {org.role === "owner" &&
                  org.organizationType !== "personal" && (
                    <Button
                      key={"transfer" + org.id}
                      color="primary"
                      onClick={() =>
                        changeOwner(org.id as string, org.name, org.ownerUserId)
                      }
                    >
                      Transfer
                    </Button>
                  )}
                {org.role === "owner" &&
                  org.organizationType !== "personal" && (
                    <Button
                      key={"delete" + org.id}
                      color="primary"
                      onClick={() => setOrgForDeletion(org)}
                    >
                      Delete
                    </Button>
                  )}
                {org.role !== "owner" &&
                  org.organizationType !== "personal" && (
                    <Button
                      key={"leave" + org.id}
                      color="primary"
                      onClick={leaveOrg(org.id as string)}
                    >
                      Leave
                    </Button>
                  )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
export default List;
